"use client";

import type { CSSProperties, FC } from "react";
import { useState } from "react";
import Image from "next/image";
import { PauseCircleIcon, PlayCircleIcon } from "@heroicons/react/24/outline";
import { withBasePath } from "@/lib/base-path";
import { cn } from "@/lib/cn";

type Tile = {
  src: string;
  bg: string;
  corner?: string;
};

const RIGHT_COLUMN: Tile[] = [
  { src: "/static/directory-hero/right-top.webp", bg: "bg-brand-blue", corner: "rounded-tr-[92px]" },
  { src: "/static/directory-hero/right-mid.webp", bg: "bg-tone-pool" },
  { src: "/static/directory-hero/right-bottom.webp", bg: "bg-tone-pool", corner: "rounded-br-[92px]" },
];

const LEFT_COLUMN: Tile[] = [
  { src: "/static/directory-hero/left-top.webp", bg: "bg-brand-blue", corner: "rounded-tl-[92px]" },
  { src: "/static/directory-hero/left-mid.webp", bg: "bg-brand-blue" },
  { src: "/static/directory-hero/left-bottom.webp", bg: "bg-tone-pool", corner: "rounded-bl-[92px]" },
];

const STRIP: Tile[] = [
  { src: "/static/directory-hero/right-top.webp", bg: "bg-brand-blue", corner: "rounded-tl-[60px]" },
  { src: "/static/directory-hero/left-mid.webp", bg: "bg-brand-blue", corner: "rounded-br-[60px]" },
  { src: "/static/directory-hero/right-bottom.webp", bg: "bg-tone-pool" },
  { src: "/static/directory-hero/left-top.webp", bg: "bg-brand-blue", corner: "rounded-tl-[60px]" },
  { src: "/static/directory-hero/right-mid.webp", bg: "bg-tone-pool", corner: "rounded-br-[60px]" },
  { src: "/static/directory-hero/left-bottom.webp", bg: "bg-tone-pool" },
];

// Mirrors the literal `h-339` / `w-138` / `h-1033` classes below — Tailwind can't read JS constants.
const TILE_HEIGHT = 339;
const TILE_GAP = 8;
const STRIP_TILE_WIDTH = 138;
const STRIP_TILE_GAP = 5;

type PlaybackProps = {
  paused: boolean;
  onToggle: () => void;
};

type HeroCollagePlayButtonProps = PlaybackProps & { className: string; iconClassName: string; glassClassName: string };

// Figma's "Glass" effect on the ring doesn't survive SVG export; the disc below approximates it.
const HeroCollagePlayButton: FC<HeroCollagePlayButtonProps> = ({ paused, onToggle, className, iconClassName, glassClassName }) => {
  const Icon = paused ? PlayCircleIcon : PauseCircleIcon;

  return (
    <button
      type="button"
      aria-label={paused ? "Play photo animation" : "Pause photo animation"}
      onClick={onToggle}
      className={cn(
        "group relative inline-flex cursor-pointer items-center justify-center rounded-full text-neutral-white motion-reduce:hidden",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute rounded-full bg-neutral-white/10 shadow-[inset_1px_1px_2px_rgba(255,255,255,0.45),inset_-1px_-1px_2px_rgba(0,0,0,0.12)] backdrop-blur-[6px] transition-colors group-hover:bg-neutral-white/20 group-active:bg-neutral-white/30",
          glassClassName,
        )}
      />
      <Icon strokeWidth={1} className={cn("relative", iconClassName)} />
    </button>
  );
};

const marqueeStyle = (distance: number, durationSeconds: number, paused: boolean, reverse = false) =>
  ({
    "--marquee-distance": `-${distance}px`,
    animationDuration: `${durationSeconds}s`,
    animationDirection: reverse ? "reverse" : "normal",
    animationPlayState: paused ? "paused" : "running",
  }) as CSSProperties;

type HeroCollageTileProps = { tile: Tile; className: string; sizes: string; lcp?: boolean };

/* `lcp` marks a marquee's first tile, the hero's LCP candidate: fetched eagerly at high priority instead of lazily.
   Not `preload` — desktop and mobile each have their own candidate, and a <head> preload would pull both. */
const HeroCollageTile: FC<HeroCollageTileProps> = ({ tile, className, sizes, lcp }) => (
  <div className={cn("relative shrink-0 overflow-hidden", className, tile.bg, tile.corner)}>
    <Image
      src={withBasePath(tile.src)}
      alt=""
      fill
      sizes={sizes}
      loading={lcp ? "eager" : undefined}
      fetchPriority={lcp ? "high" : undefined}
      className="object-cover object-bottom"
    />
  </div>
);

type HeroCollageColumnProps = {
  tiles: Tile[];
  className: string;
  direction: "up" | "down";
  durationSeconds: number;
  paused: boolean;
};

const HeroCollageColumn: FC<HeroCollageColumnProps> = ({ tiles, className, direction, durationSeconds, paused }) => (
  <div aria-hidden className={cn("pointer-events-none absolute h-1033 overflow-hidden", className)}>
    <div
      className="flex animate-[marquee-vertical_linear_infinite] flex-col gap-8 motion-reduce:animate-none"
      style={marqueeStyle(tiles.length * (TILE_HEIGHT + TILE_GAP), durationSeconds, paused, direction === "down")}
    >
      {[...tiles, ...tiles].map((tile, index) => (
        <HeroCollageTile
          key={`${tile.src}-${index}`}
          tile={tile}
          className="h-339 w-full rounded-[20px]"
          sizes="229px"
          lcp={index === 0}
        />
      ))}
    </div>
  </div>
);

export const HeroCollage: FC = () => {
  const [paused, setPaused] = useState(false);

  return (
    <div className="hidden lg:block">
      <HeroCollageColumn tiles={RIGHT_COLUMN} direction="up" durationSeconds={20} paused={paused} className="top-22 right-40 w-229" />
      <HeroCollageColumn tiles={LEFT_COLUMN} direction="down" durationSeconds={26} paused={paused} className="top-70 right-282 w-228" />
      <HeroCollagePlayButton
        paused={paused}
        onToggle={() => setPaused((value) => !value)}
        className="absolute right-56 bottom-12 size-48"
        iconClassName="size-48"
        glassClassName="inset-6"
      />
    </div>
  );
};

export const HeroCollageStrip: FC<{ className?: string }> = ({ className }) => {
  const [paused, setPaused] = useState(false);

  return (
    <div className={cn("relative lg:hidden", className)}>
      <div aria-hidden className="pointer-events-none overflow-hidden">
        <div
          className="flex w-max animate-[marquee-horizontal_linear_infinite] gap-5 motion-reduce:animate-none"
          style={marqueeStyle(STRIP.length * (STRIP_TILE_WIDTH + STRIP_TILE_GAP), 30, paused)}
        >
          {[...STRIP, ...STRIP].map((tile, index) => (
            <HeroCollageTile
              key={`${tile.src}-${index}`}
              tile={tile}
              className="h-205 w-138 rounded-[14px]"
              sizes="138px"
              lcp={index === 0}
            />
          ))}
        </div>
      </div>
      <HeroCollagePlayButton
        paused={paused}
        onToggle={() => setPaused((value) => !value)}
        className="absolute right-16 bottom-8 size-36"
        iconClassName="size-36"
        glassClassName="inset-[4.5px]"
      />
    </div>
  );
};
